import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';
import { Button, Icon } from '@blueprintjs/core';
import * as DataList from 'components/DataList';
import EditSupplierPanel from 'components/Supplier/EditSupplierPanel';
import { SupplierMaterials, SupplierPurchaseOrders } from 'components/Supplier/SupplierRecords';

const sentenceCase = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase().replace(/_/g, ' ') : '');

// A supplier's page (pages/suppliers/show.js), in the client page's style
// (client-page.scss): a header with the name and how to reach them, "Edit
// details" in a side panel, then the record counts as tabs (materials,
// purchase orders) over the selected list.
@inject('supplierStore') @observer
class SupplierProfile extends Component {
  @observable isEditOpen = false;

  componentDidMount() {
    this.props.supplierStore.loadAnalytics({ uuid: this.props.supplier.attributes.id });
  }

  renderTabs() {
    const { selectedResource, onResourceChange, supplierStore } = this.props;
    const { isLoading, isError, objects } = supplierStore.supplierAnalytics;
    const resources = objects || [];
    if (isLoading && resources.length === 0) {
      return (
        <div className="cp-tabs" aria-hidden="true">
          {[0, 1].map((index) => (
            <span key={index} className="cp-tab cp-tab--loading">
              <span className="dl-bone cp-bone--label" />
              <span className="dl-bone cp-bone--count" />
            </span>
          ))}
        </div>
      );
    }
    if (isError && resources.length === 0) {
      return (
        <p className="sup-counts-error" role="alert">
          The counts couldn't be loaded.{' '}
          <button type="button" className="up-text-button" onClick={() => supplierStore.loadAnalytics({ uuid: this.props.supplier.attributes.id })}>
            Try again
          </button>
        </p>
      );
    }
    return (
      <div className="cp-tabs" role="tablist" aria-label="Records">
        {resources.map((resource) => {
          const active = resource.id === selectedResource;
          const parts = (resource.breakdown || []).filter((part) => String(part.name).toLowerCase() !== 'all');
          return (
            <button
              key={resource.id}
              type="button"
              role="tab"
              aria-selected={active ? 'true' : 'false'}
              className={`cp-tab${active ? ' is-active' : ''}`}
              onClick={() => onResourceChange(resource.id)}
            >
              <span className="cp-tab__label">{sentenceCase(resource.name)}</span>
              <span className="cp-tab__count">{resource.total_count}</span>
              {parts.length > 0 && (
                <span className="cp-tab__parts">
                  {parts.map((part) => (
                    <span key={part.name} className="cp-tab__part">{part.count} {String(part.name).toLowerCase()}</span>
                  ))}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  renderRecords() {
    const { supplier, selectedResource } = this.props;
    if (selectedResource === 'materials') return <SupplierMaterials key="materials" supplier={supplier} />;
    if (selectedResource === 'purchase_orders') return <SupplierPurchaseOrders key="purchase_orders" supplier={supplier} />;
    return null;
  }

  render() {
    const { supplier } = this.props;
    const a = supplier.attributes;
    return (
      <div className="cp sup">
        <header className="cp-head" aria-label="Supplier">
          <span className="cp-avatar" aria-hidden="true">{DataList.initials(a.name)}</span>
          <div className="cp-head__who">
            <h1 className="cp-head__name">{a.name}</h1>
            <div className="cp-head__meta">
              {a.phone && <a href={`tel:${a.phone}`}><Icon icon="phone" iconSize={12} />{a.phone}</a>}
              {a.email && <a href={`mailto:${a.email}`}><Icon icon="envelope" iconSize={12} />{a.email}</a>}
              {a.address && (
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.address)}`} target="_blank" rel="noopener noreferrer">
                  <Icon icon="map-marker" iconSize={12} />{a.address}
                </a>
              )}
            </div>
          </div>
          <Button className="cp-head__edit" icon="edit" text="Edit details" onClick={() => { this.isEditOpen = true; }} />
        </header>

        <section className="cp-main" aria-label="Supplier records">
          {this.renderTabs()}
          <div className="cp-table">{this.renderRecords()}</div>
        </section>

        <EditSupplierPanel supplier={supplier} isOpen={this.isEditOpen} onClose={() => { this.isEditOpen = false; }} />
      </div>
    );
  }
}

// Shown while the supplier loads: the page's outline, shimmering.
export const SupplierPageSkeleton = () => (
  <div className="cp sup cp--loading" role="status" aria-label="Loading supplier">
    <div className="cp-head" aria-hidden="true">
      <span className="dl-bone cp-avatar" />
      <div className="cp-head__who">
        <span className="dl-bone" style={{ width: 220, height: 20 }} />
        <span className="dl-bone" style={{ width: 320, height: 12, marginTop: 10 }} />
      </div>
      <span className="dl-bone" style={{ width: 120, height: 36, borderRadius: 8 }} />
    </div>
    <div className="cp-tabs" aria-hidden="true">
      {[0, 1].map((index) => (
        <span key={index} className="cp-tab cp-tab--loading">
          <span className="dl-bone cp-bone--label" />
          <span className="dl-bone cp-bone--count" />
        </span>
      ))}
    </div>
  </div>
);

export default SupplierProfile;
